import { ESUTD_STAGE_LABELS } from './esutd-stages';

/**
 * Человеческие названия действий в журнале.
 *
 * В журнал пишется служебный код (`shift.driver-confirmation`), и до сих пор он же
 * показывался человеку. Диспетчер, разбирая спорную смену, читал латиницу с точками и
 * додумывал смысл — а журнал нужен ровно затем, чтобы не додумывать.
 *
 * Словарь общий для карточки смены и для раздела «Аудит»: одно и то же действие обязано
 * называться одинаково в обоих местах, иначе по описанию их не сопоставить.
 *
 * Неизвестный код показывается как есть. Это намеренно: выдумать название нельзя, а
 * скрывать запись тем более — лучше непонятный код, чем пропавшее действие.
 */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  // ── смена ──
  'shift.create': 'Смена создана',
  'shift.update': 'Смена изменена',
  'shift.confirm': 'Смена подтверждена',
  'shift.block': 'Смена заблокирована',
  'shift.close': 'Смена закрыта',
  'shift.driver-confirmation': 'Ответ водителя на наряд',
  'shift.driver-reading': 'Водитель подтвердил прочтение листа',
  'shift.depart': 'Выезд из парка зафиксирован',
  'shift.departure': 'Выезд из парка зафиксирован',
  'shift.return': 'Возврат в парк зафиксирован',
  'shift.totals': 'Итоги смены пересчитаны',
  'shift.mileage': 'Пробег уточнён',
  'shift.incident': 'Происшествие на линии',
  'shift.replace': 'Техника или водитель заменены',
  'shift.invalidate': 'Осмотр аннулирован заменой',
  'shift.readiness-recalc': 'Готовность пересчитана',

  // ── выезды внутри смены ──
  call: 'Вызов принят',
  depart: 'Выезд на объект',
  arrive: 'Прибытие на объект',
  finish: 'Работы на объекте завершены',
  return: 'Возврат с объекта',

  // ── подпись и ЕСУТД ──
  'shift.signature': 'Подпись поставлена',
  'shift.signature-failed': 'Подпись не поставлена',
  'shift.waybill-draft-failed': 'Путевой лист не зарегистрирован в ЕСУТД',
  'shift.qr-token-issue': 'Выдан QR для подписи',
  'shift.qr-token-consume': 'QR для подписи использован',

  // ── обмен с 1С и уведомления ──
  'shift.onec-fact-failed': 'Факт смены не ушёл в 1С',
  'whatsapp:bind': 'Привязан номер WhatsApp',
  'whatsapp-reply-failed': 'Ответ водителя из WhatsApp не разобран',
  'notify-failed': 'Уведомление не доставлено',

  // ── путевой лист ──
  'waybill.issue': 'Путевой лист выдан',
  'waybill.cancel': 'Путевой лист аннулирован',

  // ── общие коды справочников ──
  create: 'Создано',
  update: 'Изменено',
  delete: 'Удалено',
  append: 'Добавлено',
  status: 'Состояние изменено',

  // ── справочник техники ──
  'machine.repair': 'Техника отправлена в ремонт',

  // ── доступ ──
  login: 'Вход в систему',
  'login:failed': 'Неудачная попытка входа',
  'login:blocked': 'Вход заблокированной учётной записи',
  'password:change': 'Пароль изменён',
  'password:reset': 'Пароль сброшен',
};

/**
 * Часть кодов собирается на лету — со стадией ЕСУТД или фазой осмотра внутри:
 * `shift.technical-inspection.PRE`, `MED_PRE_ENVELOPE`, `SIGN_CARRIER_SIGNED`. Списком их
 * не покрыть, поэтому разбираем по правилам.
 */
const PHASE_LABELS: Record<string, string> = { PRE: 'Предрейсовый', POST: 'Послерейсовый' };

const SUFFIX_LABELS: readonly { suffix: string; label: (stage: string) => string }[] = [
  { suffix: '_ENVELOPE', label: (stage) => `Запрошен конверт на подпись: ${stage}` },
  { suffix: '_AWAITING_SIGNATURE', label: (stage) => `Ждёт подписи работника: ${stage}` },
  { suffix: '_SIGNED', label: (stage) => `Подписано и отправлено: ${stage}` },
  { suffix: '_RETRY', label: (stage) => `Повтор отправки: ${stage}` },
  { suffix: '_ENQUEUE_FAILED', label: (stage) => `Не удалось поставить в очередь: ${stage}` },
];

/** Название действия для показа. Незнакомый код возвращается как есть. */
export function auditActionLabel(action: string): string {
  const exact = AUDIT_ACTION_LABELS[action];
  if (exact) {
    return exact;
  }

  const exam = /^shift\.(medical-exam|technical-inspection)\.(PRE|POST)$/.exec(action);
  if (exam) {
    const what = exam[1] === 'medical-exam' ? 'медосмотр' : 'техосмотр';
    return `${PHASE_LABELS[exam[2]]} ${what}`;
  }

  for (const { suffix, label } of SUFFIX_LABELS) {
    if (action.endsWith(suffix)) {
      return label(stageLabel(action.slice(0, -suffix.length)));
    }
  }

  return stageLabel(action);
}

/** Стадия ЕСУТД по-русски; не стадия — возвращается как есть. */
function stageLabel(code: string): string {
  return (ESUTD_STAGE_LABELS as Record<string, string>)[code] ?? code;
}
